import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  BrowserDownloadEvent,
  BrowserEvent,
  BrowserHostState,
  BrowserPageTheme,
  BrowserTabState,
} from './types';

const WEB_BROWSER_SETTINGS_APP_ID = 'web-browser';
const WEB_BROWSER_SETTINGS_STORAGE_KEY = 'maplebrowser.settings';
const DEFAULT_BROWSER_THEME: BrowserPageTheme = 'light';
const DEFAULT_BROWSER_URL = 'https://duckduckgo.com/';

type BrowserStoredSettings = {
  pageTheme?: BrowserPageTheme;
  homepage?: string;
};

const normalizeBrowserTheme = (value: unknown): BrowserPageTheme => {
  if (value === 'light' || value === 'dark' || value === 'system') {
    return value;
  }
  return DEFAULT_BROWSER_THEME;
};

const normalizeHomepage = (value: unknown) => {
  const raw = typeof value === 'string' ? value.trim() : '';

  if (!raw) {
    return DEFAULT_BROWSER_URL;
  }

  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(raw)) {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      throw new Error('Homepage must use http or https.');
    }
    return parsed.toString();
  }

  if (/\s/.test(raw)) {
    throw new Error('Homepage must be a website URL.');
  }

  return new URL(`https://${raw}`).toString();
};

const getInitialUrl = () => {
  try {
    const raw = localStorage.getItem('maplebrowser.lastContext');
    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as { url?: string };
    return typeof parsed?.url === 'string' && parsed.url ? parsed.url : undefined;
  } catch {
    return undefined;
  }
};

const readFallbackSettings = (): Required<BrowserStoredSettings> => {
  try {
    const raw = localStorage.getItem(WEB_BROWSER_SETTINGS_STORAGE_KEY);
    if (!raw) {
      return {
        pageTheme: DEFAULT_BROWSER_THEME,
        homepage: DEFAULT_BROWSER_URL,
      };
    }

    const parsed = JSON.parse(raw) as BrowserStoredSettings;
    return {
      pageTheme: normalizeBrowserTheme(parsed?.pageTheme),
      homepage: normalizeHomepage(parsed?.homepage),
    };
  } catch {
    return {
      pageTheme: DEFAULT_BROWSER_THEME,
      homepage: DEFAULT_BROWSER_URL,
    };
  }
};

const persistFallbackSettings = (settings: Required<BrowserStoredSettings>) => {
  try {
    localStorage.setItem(WEB_BROWSER_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
  }
};

export const useBrowserTabs = (windowId: string) => {
  const browserApi = window.electronAPI?.browser;
  const settingsApi = window.electronAPI;
  const isSupported = Boolean(window.electronAPI?.isElectron && browserApi);
  const [state, setState] = useState<BrowserHostState | null>(null);
  const [lastDownloadEvent, setLastDownloadEvent] = useState<BrowserDownloadEvent | null>(null);
  const [browserTheme, setBrowserThemeState] = useState<BrowserPageTheme>(DEFAULT_BROWSER_THEME);
  const [homepage, setHomepageState] = useState(DEFAULT_BROWSER_URL);
  const [privateMode, setPrivateModeState] = useState(false);
  const [isThemeReady, setIsThemeReady] = useState(false);
  const recoveringMissingTabRef = useRef(false);
  const browserThemeRef = useRef<BrowserPageTheme>(DEFAULT_BROWSER_THEME);
  const homepageRef = useRef(DEFAULT_BROWSER_URL);
  const privateModeRef = useRef(false);

  const applyHostState = useCallback((nextState: BrowserHostState | null) => {
    if (!nextState) {
      return;
    }

    setState(nextState);
    setBrowserThemeState(nextState.theme);
    setPrivateModeState(Boolean(nextState.privateMode));
  }, []);

  useEffect(() => {
    browserThemeRef.current = browserTheme;
  }, [browserTheme]);

  useEffect(() => {
    homepageRef.current = homepage;
  }, [homepage]);

  useEffect(() => {
    privateModeRef.current = privateMode;
  }, [privateMode]);

  const ensureHostHasTab = useCallback(async () => {
    if (!browserApi) {
      return null;
    }

    const initialUrl = getInitialUrl() || homepageRef.current;

    let nextState = await browserApi.createHost({
      hostId: windowId,
      initialUrl,
      initialTheme: browserThemeRef.current,
      initialPrivateMode: privateModeRef.current,
    });
    applyHostState(nextState);

    if (nextState?.tabs.length) {
      return nextState;
    }

    nextState = await browserApi.createTab({
      hostId: windowId,
      url: initialUrl,
      activate: true,
    });
    applyHostState(nextState);

    if (nextState?.tabs.length) {
      return nextState;
    }

    await browserApi.destroyHost(windowId).catch(() => {});

    nextState = await browserApi.createHost({
      hostId: windowId,
      initialUrl,
      initialTheme: browserThemeRef.current,
      initialPrivateMode: privateModeRef.current,
    });
    applyHostState(nextState);

    if (nextState?.tabs.length) {
      return nextState;
    }

    nextState = await browserApi.createTab({
      hostId: windowId,
      url: initialUrl,
      activate: true,
    });
    applyHostState(nextState);

    return nextState;
  }, [applyHostState, browserApi, windowId]);

  const recoverMissingTab = useCallback(async () => {
    if (!browserApi || recoveringMissingTabRef.current) {
      return;
    }

    recoveringMissingTabRef.current = true;

    try {
      await ensureHostHasTab();
    } catch (error) {
      console.error('Failed to recover missing Navigator tab:', error);
    } finally {
      window.setTimeout(() => {
        recoveringMissingTabRef.current = false;
      }, 250);
    }
  }, [browserApi, ensureHostHasTab]);

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      try {
        const settings = await settingsApi?.loadSettings?.(WEB_BROWSER_SETTINGS_APP_ID);
        if (!isMounted) {
          return;
        }

        setBrowserThemeState(normalizeBrowserTheme(settings?.pageTheme));
        setHomepageState(normalizeHomepage(settings?.homepage));
      } catch {
        if (isMounted) {
          const fallbackSettings = readFallbackSettings();
          setBrowserThemeState(fallbackSettings.pageTheme);
          setHomepageState(fallbackSettings.homepage);
        }
      } finally {
        if (isMounted) {
          setIsThemeReady(true);
        }
      }
    };

    loadTheme();

    return () => {
      isMounted = false;
    };
  }, [settingsApi]);

  useEffect(() => {
    if (!isSupported || !browserApi || !isThemeReady) {
      return;
    }

    let isMounted = true;
    const unsubscribe = browserApi.onEvent((event: BrowserEvent) => {
      if (event.hostId !== windowId) {
        return;
      }

      if (event.type === 'state' && isMounted) {
        applyHostState(event.state);

        if (event.state.tabs.length === 0) {
          recoverMissingTab().catch(() => {});
        }
      }

      if (event.type === 'download' && isMounted) {
        setLastDownloadEvent(event.download);
      }
    });

    ensureHostHasTab().then((nextState) => {
      if (isMounted && nextState?.tabs.length === 0) {
        recoverMissingTab().catch(() => {});
      }
    }).catch((error) => {
      console.error('Failed to create Navigator host:', error);
    });

    return () => {
      isMounted = false;
      unsubscribe();
      browserApi.setBounds({
        hostId: windowId,
        bounds: { x: 0, y: 0, width: 0, height: 0 },
        visible: false,
      }).catch(() => {});
      browserApi.destroyHost(windowId).catch(() => {});
    };
  }, [applyHostState, browserApi, ensureHostHasTab, isSupported, isThemeReady, recoverMissingTab, windowId]);

  const activeTab = useMemo<BrowserTabState | null>(() => {
    if (!state?.activeTabId) {
      return state?.tabs[0] || null;
    }

    return state.tabs.find((tab) => tab.id === state.activeTabId) || state.tabs[0] || null;
  }, [state]);

  useEffect(() => {
    if (!activeTab?.url || privateMode) {
      return;
    }

    try {
      localStorage.setItem('maplebrowser.lastContext', JSON.stringify({
        url: activeTab.url,
        updatedAt: new Date().toISOString(),
      }));
    } catch {
    }
  }, [activeTab?.url, privateMode]);

  const createTab = useCallback(async (url?: string) => {
    if (!browserApi) {
      return;
    }
    const nextState = await browserApi.createTab({ hostId: windowId, url, activate: true });
    applyHostState(nextState);
  }, [applyHostState, browserApi, windowId]);

  const closeTab = useCallback(async (tabId: string) => {
    if (!browserApi) {
      return;
    }
    const nextState = await browserApi.closeTab({ hostId: windowId, tabId });
    applyHostState(nextState);
  }, [applyHostState, browserApi, windowId]);

  const activateTab = useCallback(async (tabId: string) => {
    if (!browserApi) {
      return;
    }
    const nextState = await browserApi.activateTab({ hostId: windowId, tabId });
    applyHostState(nextState);
  }, [applyHostState, browserApi, windowId]);

  const navigateActiveTab = useCallback(async (url: string) => {
    if (!browserApi || !activeTab) {
      return;
    }
    const nextState = await browserApi.navigate({ hostId: windowId, tabId: activeTab.id, url });
    applyHostState(nextState);
  }, [activeTab, applyHostState, browserApi, windowId]);

  const goBack = useCallback(async () => {
    if (!browserApi || !activeTab) {
      return;
    }
    const nextState = await browserApi.goBack({ hostId: windowId, tabId: activeTab.id });
    applyHostState(nextState);
  }, [activeTab, applyHostState, browserApi, windowId]);

  const goForward = useCallback(async () => {
    if (!browserApi || !activeTab) {
      return;
    }
    const nextState = await browserApi.goForward({ hostId: windowId, tabId: activeTab.id });
    applyHostState(nextState);
  }, [activeTab, applyHostState, browserApi, windowId]);

  const reload = useCallback(async () => {
    if (!browserApi || !activeTab) {
      return;
    }
    const nextState = await browserApi.reload({ hostId: windowId, tabId: activeTab.id });
    applyHostState(nextState);
  }, [activeTab, applyHostState, browserApi, windowId]);

  const stop = useCallback(async () => {
    if (!browserApi || !activeTab) {
      return;
    }
    const nextState = await browserApi.stop({ hostId: windowId, tabId: activeTab.id });
    applyHostState(nextState);
  }, [activeTab, applyHostState, browserApi, windowId]);

  const setBounds = useCallback(async (bounds: { x: number; y: number; width: number; height: number }, visible: boolean) => {
    if (!browserApi) {
      return;
    }

    await browserApi.setBounds({
      hostId: windowId,
      bounds,
      visible,
    });
  }, [browserApi, windowId]);

  const setBrowserTheme = useCallback(async (theme: BrowserPageTheme) => {
    const nextTheme = normalizeBrowserTheme(theme);
    setBrowserThemeState(nextTheme);
    browserThemeRef.current = nextTheme;

    const fallbackSettings = readFallbackSettings();
    persistFallbackSettings({
      pageTheme: nextTheme,
      homepage: homepageRef.current || fallbackSettings.homepage,
    });

    try {
      const currentSettings = await settingsApi?.loadSettings?.(WEB_BROWSER_SETTINGS_APP_ID);
      await settingsApi?.saveSettings?.(WEB_BROWSER_SETTINGS_APP_ID, {
        ...(currentSettings || {}),
        pageTheme: nextTheme,
        homepage: homepageRef.current,
      });
    } catch {
    }

    if (!browserApi || !isThemeReady) {
      return;
    }

    try {
      const nextState = await browserApi.setTheme({ hostId: windowId, theme: nextTheme });
      applyHostState(nextState);

      if (nextState?.tabs.length === 0) {
        recoverMissingTab().catch(() => {});
      }
    } catch (error) {
      console.error('Failed to update Navigator page theme:', error);
    }
  }, [applyHostState, browserApi, isThemeReady, recoverMissingTab, settingsApi, windowId]);

  const setHomepage = useCallback(async (value: string) => {
    const nextHomepage = normalizeHomepage(value);
    setHomepageState(nextHomepage);
    homepageRef.current = nextHomepage;

    persistFallbackSettings({
      pageTheme: browserThemeRef.current,
      homepage: nextHomepage,
    });

    try {
      const currentSettings = await settingsApi?.loadSettings?.(WEB_BROWSER_SETTINGS_APP_ID);
      await settingsApi?.saveSettings?.(WEB_BROWSER_SETTINGS_APP_ID, {
        ...(currentSettings || {}),
        pageTheme: browserThemeRef.current,
        homepage: nextHomepage,
      });
    } catch {
    }
  }, [settingsApi]);

  const setPrivateMode = useCallback(async (nextPrivateMode: boolean) => {
    setPrivateModeState(nextPrivateMode);
    privateModeRef.current = nextPrivateMode;

    if (!browserApi || !isThemeReady) {
      return;
    }

    try {
      const nextState = await browserApi.setPrivateMode({ hostId: windowId, privateMode: nextPrivateMode });
      applyHostState(nextState);
    } catch (error) {
      console.error('Failed to update Navigator private mode:', error);
      setPrivateModeState((current) => !current);
    }
  }, [applyHostState, browserApi, isThemeReady, windowId]);

  return {
    isSupported,
    isThemeReady,
    state,
    activeTab,
    browserTheme,
    homepage,
    privateMode,
    lastDownloadEvent,
    clearLastDownloadEvent: () => setLastDownloadEvent(null),
    createTab,
    closeTab,
    activateTab,
    navigateActiveTab,
    goBack,
    goForward,
    reload,
    stop,
    setBounds,
    setBrowserTheme,
    setHomepage,
    setPrivateMode,
  };
};